import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Table, KanbanSquare, ArrowLeft, ChevronDown, Mail, CalendarClock } from "lucide-react";
import { CandidateAvatar, CandidatePanel } from "./CandidateComponents";
import AddToSequenceModal from "./sequencing/AddToSequenceModal.jsx";
import PreviewModal from "./sequencing/PreviewModal.jsx";
import useScheduleInterview from "./projects/useScheduleInterview";
import Pagination from "../../components/shared_components/Pagination";
import useServerPagination from "../hooks/useServerPagination";

// Our pipeline statuses (mirrors STATUS_OPTIONS in CandidateComponents), in board order.
const STATUS_OPTIONS = [
    { value: "not_contacted",  label: "Not Contacted",  color: "#93c5fd" },
    { value: "email_sent",     label: "Email Sent",     color: "#0d9488" },
    { value: "interested",     label: "Interested",     color: "#a78bfa" },
    { value: "interviewing",   label: "Interviewing",   color: "#fbbf24" },
    { value: "rejected",       label: "Rejected",       color: "#ef4444" },
    { value: "not_interested", label: "Not Interested", color: "#9ca3af" },
];
const statusMeta = (value) => STATUS_OPTIONS.find(o => o.value === value) ?? STATUS_OPTIONS[0];

function timeAgo(dateStr) {
    if (!dateStr) return "—";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days <= 0) return "today";
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    return `${Math.floor(months / 12)} year${Math.floor(months / 12) === 1 ? "" : "s"} ago`;
}

const LinkedInIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
);

/* ── Status pill with dropdown (shared by table + board) ───────────────────── */
const StatusPill = ({ status, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const meta = statusMeta(status);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
            >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                {meta.label}
                <ChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            </button>
            {open && (
                <div className="absolute z-20 mt-1 w-40 bg-white dark:bg-[#1B1524] rounded-xl border border-gray-100 dark:border-gray-700/50 shadow-lg py-1">
                    {STATUS_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            onClick={(e) => { e.stopPropagation(); setOpen(false); onChange(opt.value); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-all text-left ${status === opt.value ? "bg-gray-50 font-medium" : "text-gray-700"}`}
                        >
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ProfileLinks = ({ candidate }) => (
    <div className="flex items-center gap-2">
        {candidate.linkedin_url && (
            <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#0a66c2] hover:opacity-80" title="LinkedIn">
                <LinkedInIcon />
            </a>
        )}
        {candidate.email && (
            <a href={`mailto:${candidate.email}`} onClick={(e) => e.stopPropagation()}
                className="text-gray-400 dark:text-gray-500 hover:text-purple-600" title={candidate.email}>
                <Mail className="w-4 h-4" />
            </a>
        )}
    </div>
);

const Shortlist = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const query = location.state?.query || "";
    const searchId = location.state?.search_id || null;
    const jobId = location.state?.job_id || null;
    // Carried over by whoever linked here; what {{job_role}} resolves to when enrolling.
    const jobRole = location.state?.job_role || null;
    const searchName = location.state?.searchName || null;
    const orgId = useSelector(state => state.userSlice?.org_id);
    const recruiterName = useSelector(state => state.userSlice?.name) || localStorage.getItem("username") || "";

    const [candidates, setCandidates] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("table");      // "table" | "board"
    const [shortlistedMap, setShortlistedMap] = useState({});
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [addToSeqCandidates, setAddToSeqCandidates] = useState(null);
    const [previewState, setPreviewState] = useState(null);
    const [enrollResult, setEnrollResult] = useState(null);
    const [dragOverCol, setDragOverCol] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    // Scheduled candidates move to Interview Management, so the shortlist no longer holds
    // them — refetch rather than splicing, so the page backfills from the server.
    const { startBulkSchedule, scheduleUI, canSchedule } = useScheduleInterview(jobId, () => {
        setSelectedIds(new Set());
        setSelectedCandidate(null);
        fetchShortlisted();
    });

    /* ── pagination ────────────────────────────────────────────────────────────
       The table pages server-side (one page + a total per request). The board
       groups every candidate into status columns, so it asks for the full list —
       the API returns everything when `limit` is omitted. */
    const {
        currentPage,
        itemsPerPage,
        handlePageChange,
        handleItemsPerPageChange,
    } = useServerPagination({ totalItems: total, resetKey: viewMode });

    const fetchShortlisted = useCallback(async () => {
        const currentOrgId = orgId || localStorage.getItem("current_org_id");
        if (!currentOrgId) return;
        setLoading(true);
        setError(null);
        try {
            const params = { organisation_id: currentOrgId };
            if (searchId) params.search_id = searchId;
            else if (jobId) params.job_id = jobId;
            if (viewMode === "table") {
                params.page = currentPage;
                params.limit = itemsPerPage;
            }
            const { data } = await axios.get(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist`,
                { params }
            );
            const list = data.candidates ?? data ?? [];
            setCandidates(list.map(c => ({ ...c, status: c.status || "not_contacted" })));
            setTotal(data.total ?? list.length);
            setShortlistedMap(Object.fromEntries(list.map(c => [c.candidate_id, true])));
        } catch {
            setError("Failed to load shortlisted candidates. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [orgId, searchId, jobId, viewMode, currentPage, itemsPerPage]);

    useEffect(() => { fetchShortlisted(); }, [fetchShortlisted]);

    const handleFindSimilar = (candidate) => {
        const yearsExp = candidate.years_of_experience;
        const loc = candidate.location_city || candidate.location_state || candidate.location_country || null;
        const skills = candidate.skills || [];

        const builtQuery = {
            roles: [candidate.current_title].filter(Boolean),
            skills_required: skills.slice(0, 5),
            skills_nice_to_have: skills.slice(5, 10),
            locations: loc ? [loc] : [],
            seniority: candidate.seniority || null,
            experience_min: yearsExp != null ? Math.max(0, yearsExp - 3) : null,
            experience_max: yearsExp != null ? yearsExp + 3 : null,
            companies: [],
            limit: 10,
        };

        const displayQuery = `Similar profiles to ${candidate.full_name}${candidate.current_title ? ` – ${candidate.current_title}` : ""}`;

        setSelectedCandidate(null);
        navigate("/org-admin/sourcing/results", {
            state: {
                query: displayQuery,
                canonical_query: builtQuery,
                source_candidate: { name: candidate.full_name, title: candidate.current_title },
            },
        });
    };

    const handleShortlistToggle = async (candidate) => {
        const currentOrgId = localStorage.getItem("current_org_id");
        const adminId = localStorage.getItem("org_admin_id");
        const isShortlisted = shortlistedMap[candidate.candidate_id];
        // Shortlists are per-search. Use this view's search when it has one (search-scoped
        // view); in the job-scoped view fall back to the search the candidate was
        // shortlisted from, so we don't orphan the row or wipe it from other searches.
        const effectiveSearchId = searchId || candidate.search_id || null;
        try {
            if (isShortlisted) {
                await axios.delete(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist/${candidate.candidate_id}`,
                    { params: { organisation_id: currentOrgId, recruiter_id: adminId, recruiter_name: recruiterName, search_id: effectiveSearchId } }
                );
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: false }));
                setCandidates(prev => prev.filter(c => c.candidate_id !== candidate.candidate_id));
                // Only this page is in memory — refetch so the freed row is backfilled.
                fetchShortlisted();
            } else {
                await axios.post(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist`,
                    { candidate_id: candidate.candidate_id, organisation_id: currentOrgId, recruiter_id: adminId, recruiter_name: recruiterName, search_id: effectiveSearchId }
                );
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: true }));
            }
        } catch (err) {
            if (err.response?.status === 409) setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: true }));
            if (err.response?.status === 404) setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: false }));
        }
    };

    // Persist a pipeline status change and update local state optimistically.
    // Pipeline status is a standalone outreach stage — independent of shortlist/saved.
    const handleStatusChange = (candidate, newStatus) => {
        if (newStatus === candidate.status) return;
        const recruiterId = localStorage.getItem("org_admin_id");
        const currentOrgId = localStorage.getItem("current_org_id");

        setCandidates(prev => prev.map(c =>
            c.candidate_id === candidate.candidate_id ? { ...c, status: newStatus } : c
        ));

        // Status is per-role — file it against the job this shortlist belongs to.
        axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/pipeline`, {
            candidate_id: candidate.candidate_id, recruiter_id: recruiterId,
            organisation_id: currentOrgId, action: newStatus, job_id: jobId,
        }).catch(() => {});
        axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/activity`, {
            action: "status_changed", candidate_id: candidate.candidate_id, recruiter_id: recruiterId,
            organisation_id: currentOrgId,
            metadata: {
                recruiter_name: recruiterName, candidate_name: candidate.full_name,
                new_status: newStatus, new_status_label: statusMeta(newStatus).label,
                job_id: jobId,
            },
        }).catch(() => {});
    };

    /* ── header (back + query) ─────────────────────────────────────────────── */
    const TopBar = (
        <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-all">
                <ArrowLeft className="w-5 h-5" />
            </button>
            <p className="font-sans font-normal text-[20px] leading-[14px] tracking-[0] dark:text-[#FFFFFF]/90 text-textPrimary py-2">Shortlisted {searchName ? ` · ${searchName}` : ""}</p>
            {query && (
                <div className="flex-1 px-5 py-2.5 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-600 dark:text-gray-300 truncate">
                    {query}
                </div>
            )}
        </div>
    );

    /* ── view toggle (top-right) ───────────────────────────────────────────── */
    const ViewToggle = (
        <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5">
            <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "table" ? "bg-purple-100 text-purple-700" : "text-gray-400 hover:text-gray-600"}`}
            >
                <Table className="w-4 h-4" /> Table
            </button>
            <button
                onClick={() => setViewMode("board")}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "board" ? "bg-purple-100 text-purple-700" : "text-gray-400 hover:text-gray-600"}`}
            >
                <KanbanSquare className="w-4 h-4" /> Board
            </button>
        </div>
    );

    // Take the row from the list, not the object the panel was opened with, so an email
    // added in the panel is on it — the enroll gate reads these fields to decide readiness.
    const startEnroll = (c) => {
        const row = candidates.find(r => r.candidate_id === c.candidate_id);
        setAddToSeqCandidates([row || c]);
    };

    // A contact value revealed or added in the panel belongs on the row too, so the
    // enroll gate and the row behind the panel both see it without a refetch.
    const patchCandidateContact = (candidateId, changes) => {
        setCandidates(prev => prev.map(c =>
            c.candidate_id === candidateId ? { ...c, ...changes } : c
        ));
        setSelectedCandidate(prev =>
            prev?.candidate_id === candidateId ? { ...prev, ...changes } : prev
        );
    };

    /* ── selection ─────────────────────────────────────────────────────────── */
    // Selection is page-scoped: `candidates` is the page the server returned, so both
    // the header checkbox and "Select all" reach exactly the rows on screen.
    const allOnPageSelected =
        candidates.length > 0 && candidates.every(c => selectedIds.has(c.candidate_id));
    const allSelected = allOnPageSelected;

    const togglePage = () =>
        setSelectedIds(prev => {
            const next = new Set(prev);
            candidates.forEach(c =>
                allOnPageSelected ? next.delete(c.candidate_id) : next.add(c.candidate_id)
            );
            return next;
        });
    const toggleAll = togglePage;
    const toggleOne = (id) =>
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    /* ── table view ────────────────────────────────────────────────────────── */
    const TableView = (
        <>
        <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs font-medium text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700/50">
                            <th className="pl-6 pr-2 py-3 w-10">
                                <input
                                    type="checkbox"
                                    checked={allOnPageSelected}
                                    ref={el => {
                                        if (el) el.indeterminate =
                                            !allOnPageSelected && candidates.some(c => selectedIds.has(c.candidate_id));
                                    }}
                                    onChange={togglePage}
                                    className="accent-purple-600 cursor-pointer"
                                />
                            </th>
                            <th className="px-6 py-3 font-medium">Full Name</th>
                            <th className="px-4 py-3 font-medium">Profiles</th>
                            <th className="px-4 py-3 font-medium">Status</th>
                            <th className="px-4 py-3 font-medium">Owner</th>
                            <th className="px-4 py-3 font-medium">Date</th>
                            <th className="px-4 py-3 font-medium">Current Role</th>
                            <th className="px-4 py-3 font-medium">Organization</th>
                        </tr>
                    </thead>
                    <tbody>
                        {candidates.map((c) => (
                            <tr
                                key={c.candidate_id}
                                onClick={() => setSelectedCandidate(c)}
                                className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-all"
                            >
                                <td className="pl-6 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(c.candidate_id)}
                                        onChange={() => toggleOne(c.candidate_id)}
                                        className="accent-purple-600 cursor-pointer"
                                    />
                                </td>
                                <td className="px-6 py-3">
                                    <div className="flex items-center gap-3">
                                        <CandidateAvatar candidate={c} size={32} />
                                        <span className="font-medium text-gray-900 dark:text-white whitespace-nowrap">{c.full_name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3"><ProfileLinks candidate={c} /></td>
                                <td className="px-4 py-3">
                                    <StatusPill status={c.status} onChange={(s) => handleStatusChange(c, s)} />
                                </td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{c.owner || "—"}</td>
                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{timeAgo(c.saved_at)}</td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-[200px] truncate" title={c.current_title || ""}>
                                    {c.current_title || "—"}
                                </td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-[180px] truncate" title={c.current_company || ""}>
                                    {c.current_company || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
        <div className="pt-6">
            <Pagination
                currentPage={currentPage}
                totalItems={total}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                label="Candidates"
            />
        </div>
        </>
    );

    /* ── board (kanban) view ───────────────────────────────────────────────── */
    const BoardView = (
        <div className="flex gap-4 overflow-x-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {STATUS_OPTIONS.map(col => {
                const colCandidates = candidates.filter(c => c.status === col.value);
                const isOver = dragOverCol === col.value;
                return (
                    <div
                        key={col.value}
                        onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.value); }}
                        onDragLeave={() => setDragOverCol(prev => (prev === col.value ? null : prev))}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOverCol(null);
                            const id = e.dataTransfer.getData("text/plain");
                            const cand = candidates.find(c => c.candidate_id === id);
                            if (cand) handleStatusChange(cand, col.value);
                        }}
                        className={`flex-shrink-0 w-72 rounded-2xl p-3 transition-all ${isOver ? "bg-purple-50 ring-2 ring-purple-200" : "bg-gray-50"}`}
                    >
                        <div className="flex items-center gap-2 px-1 pb-3">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{col.label}</h3>
                            <span className="text-xs text-gray-400 dark:text-gray-500">{colCandidates.length}</span>
                        </div>

                        <div className="flex flex-col gap-2.5 min-h-[80px]">
                            {colCandidates.map(c => (
                                <div
                                    key={c.candidate_id}
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData("text/plain", c.candidate_id)}
                                    onClick={() => setSelectedCandidate(c)}
                                    className="bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 cursor-pointer hover:shadow-sm hover:border-purple-100 transition-all"
                                >
                                    <div className="flex items-start gap-2.5">
                                        <CandidateAvatar candidate={c} size={32} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.full_name}</p>
                                            {c.current_title && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.current_title}</p>
                                            )}
                                            {c.current_company && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{c.current_company}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-2.5">
                                        <ProfileLinks candidate={c} />
                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">{timeAgo(c.saved_at)}</span>
                                    </div>
                                </div>
                            ))}
                            {colCandidates.length === 0 && (
                                <div className="text-center text-xs text-gray-300 dark:text-gray-600 py-6">Drop candidates here</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
            {TopBar}

            <div className="w-full px-6 py-6">
                {loading && (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-400 dark:text-gray-500">Loading shortlisted candidates...</p>
                    </div>
                )}

                {error && !loading && (
                    <div className="text-center py-24">
                        <p className="text-sm text-red-400">{error}</p>
                        <button
                            onClick={fetchShortlisted}
                            className="mt-4 px-4 py-2 rounded-full text-sm text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30 hover:bg-purple-50"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <p className="font-sans font-medium text-sm leading-[14px] tracking-[0] dark:text-[#FFFFFF]/90 text-textPrimary py-2">Shortlist ({total})</p>
                            {ViewToggle}
                        </div>

                        {selectedIds.size > 0 && viewMode === "table" && (
                            <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-white dark:bg-white/5 border border-purple-100 dark:border-purple-500/30 rounded-xl shadow-sm">
                                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{selectedIds.size} selected</span>
                                <button
                                    onClick={toggleAll}
                                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline-offset-2 hover:underline"
                                >
                                    {allSelected ? "Deselect all" : `Select all ${candidates.length} on this page`}
                                </button>
                                <div className="flex-1" />
                                <button
                                    onClick={() => setAddToSeqCandidates(candidates.filter(c => selectedIds.has(c.candidate_id)))}
                                    className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-all"
                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    + Add to Outreach ({selectedIds.size})
                                </button>
                                {canSchedule && (
                                    <button
                                        onClick={() => startBulkSchedule(candidates.filter(c => selectedIds.has(c.candidate_id)))}
                                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-all"
                                    >
                                        Schedule Interview ({selectedIds.size})
                                    </button>
                                )}
                                <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 px-2">✕</button>
                            </div>
                        )}

                        {total === 0 ? (
                            <div className="text-center py-24">
                                <p className="text-gray-400 dark:text-gray-500">No shortlisted candidates yet.</p>
                            </div>
                        ) : (
                            viewMode === "table" ? TableView : BoardView
                        )}
                    </>
                )}
            </div>

            {selectedCandidate && (
                <CandidatePanel
                    candidate={selectedCandidate}
                    onClose={() => setSelectedCandidate(null)}
                    shortlisted={shortlistedMap[selectedCandidate.candidate_id] ?? true}
                    onShortlist={handleShortlistToggle}
                    onEnroll={startEnroll}
                    onContactUpdate={patchCandidateContact}
                    onFindSimilar={handleFindSimilar}
                />
            )}

            {addToSeqCandidates && (
                <AddToSequenceModal
                    candidates={addToSeqCandidates}
                    jobId={jobId}
                    jobRole={jobRole}
                    onClose={() => setAddToSeqCandidates(null)}
                    onPreview={(seq, list) => {
                        setPreviewState({ candidates: list, sequence: seq });
                        setAddToSeqCandidates(null);
                    }}
                    onEnrolled={(summary) => {
                        setAddToSeqCandidates(null);
                        setSelectedIds(new Set());
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}

            {previewState && (
                <PreviewModal
                    candidates={previewState.candidates}
                    sequence={previewState.sequence}
                    jobId={jobId}
                    jobRole={jobRole}
                    onClose={() => setPreviewState(null)}
                    onBack={() => { setAddToSeqCandidates(previewState.candidates); setPreviewState(null); }}
                    onEnroll={(summary) => {
                        setPreviewState(null);
                        setSelectedIds(new Set());
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}
            {enrollResult && (
                <div className={`fixed top-5 right-5 z-[100] flex items-start gap-2 px-4 py-3 bg-white rounded-xl shadow-lg border text-sm max-w-sm ${
                    enrollResult.tone === "error" ? "border-red-200 text-red-700"
                        : enrollResult.tone === "warn" ? "border-amber-200 text-amber-700"
                        : "border-green-200 text-green-700"
                }`}>
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {enrollResult.tone === "success"
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />}
                    </svg>
                    {enrollResult.message}
                </div>
            )}

            {scheduleUI}
        </div>
    );
};

export default Shortlist;
